import React, {Fragment, useRef, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleCheck} from "@fortawesome/free-solid-svg-icons";
import {toast} from "react-toastify";
import Box from "../UI/Box";
import ListEdit from "../UI/ListEdit";
import {useGoogleReviewRequests} from "../../hooks/useGoogleReviewRequests";
import {updateGoogleReviewGiven} from "../../api/googleReviewApiClient";
import {convertToDisplayDateTime} from "../../utility/utility";

const GoogleReviewRequestList = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [submitCount, setSubmitCount] = useState(0);
    const [filterEmail, setFilterEmail] = useState(null);
    const [filterCustomerName, setFilterCustomerName] = useState(null);
    const [filterReviewGiven, setFilterReviewGiven] = useState(null);
    const emailFilterRef = useRef(null);
    const customerNameFilterRef = useRef(null);
    const reviewGivenFilterRef = useRef(null);
    const {googleReviewRequests, pagination} = useGoogleReviewRequests(page, pageSize, filterEmail, filterCustomerName, filterReviewGiven, submitCount);

    const handleReviewGivenChange = (googleReviewRequestId, reviewGiven) => {
        updateGoogleReviewGiven(googleReviewRequestId, reviewGiven)
            .then(() => {
                setSubmitCount(prevCount => prevCount + 1);
                toast.success('Success!');
            })
            .catch(
                (error) => {
                    toast.error(error.message);
                }
            );
    };

    const handleUndoReviewGivenClick = (googleReviewRequestId) => {
        // eslint-disable-next-line no-restricted-globals
        if (confirm('Are you sure?')) {
            handleReviewGivenChange(googleReviewRequestId, false);
        }
    };

    const handleEmailFilterChange = () => {
        setPage(1);
        setFilterEmail(emailFilterRef.current.value);
    };

    const handleCustomerNameFilterChange = () => {
        setPage(1);
        setFilterCustomerName(customerNameFilterRef.current.value);
    };

    const handleReviewGivenFilterChange = () => {
        setPage(1);
        setFilterReviewGiven(reviewGivenFilterRef.current.value === '' ? null : reviewGivenFilterRef.current.value);
    };

    const handlePageChange = (pageNumber) => {
        setPage(pageNumber);
    };

    const handlePageSizeChange = (pageSize) => {
        setPageSize(pageSize);
        setPage(1);
    };

    const googleReviewRequestList = googleReviewRequests.map((item) => ({
        googleReviewRequestId : item.googleReviewRequestId,
        email                 : item.email,
        customerName          : item.customerName ? item.customerName : <span className="text-gray-300">—</span>,
        sentAt                : convertToDisplayDateTime(item.sentAt),
        sendCount             : item.sendCount,
        reviewGiven           : item.reviewGiven
            ? <span
                title="Click to undo"
                onClick={() => handleUndoReviewGivenClick(item.googleReviewRequestId)}
                className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700 hover:cursor-pointer hover:bg-green-200"
            >
                <FontAwesomeIcon icon={faCircleCheck} className="text-[11px]"/>
                Reviewed
            </span>
            : <button
                type="button"
                onClick={() => handleReviewGivenChange(item.googleReviewRequestId, true)}
                className="rounded-full border border-pink-300 bg-pink-50 px-2.5 py-1 text-xs font-semibold text-pink-600 transition-colors hover:cursor-pointer hover:bg-pink-100"
            >
                Mark reviewed
            </button>
    }));

    const listHeaders = [
        'Email',
        'Customer Name',
        'Requested At',
        'Requests Sent',
        'Review'
    ];

    const columns = {
        functionParams : ['googleReviewRequestId'],
        dataKeys       : [
            'email',
            'customerName',
            'sentAt',
            'sendCount',
            'reviewGiven'
        ],
    }

    return (
        <Fragment>
            <Box>
                <div className="flex flex-wrap gap-3 my-4">
                    <input ref={emailFilterRef} type="text" placeholder="Filter by email" className="border p-2" onChange={handleEmailFilterChange}/>
                    <input ref={customerNameFilterRef} type="text" placeholder="Filter by customer name" className="border p-2" onChange={handleCustomerNameFilterChange}/>
                    <select ref={reviewGivenFilterRef} onChange={handleReviewGivenFilterChange} className="border p-2">
                        <option value="">Filter by review</option>
                        <option value="1">Reviewed</option>
                        <option value="0">Not reviewed</option>
                    </select>
                </div>
                <ListEdit
                    headers={listHeaders}
                    items={googleReviewRequestList}
                    columns={columns}
                    pagination={pagination}
                    handlePageChange={handlePageChange}
                    handlePageSizeChange={handlePageSizeChange}
                />
            </Box>
        </Fragment>
    );
}

export default GoogleReviewRequestList;
